import type { TFunction } from 'i18next'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { useSession } from '@/features/auth/api/useSession'
import {
  legDraftForCase,
  legDraftFrom,
  legUpdatePayload,
  toLegPayload,
  useAddLeg,
  useCase,
  useCaseExpenseLines,
  useRecordExpenseBatch,
  useSetExpenseDetailOf,
  useUpdateLeg,
  type LegEdits,
} from '@/features/cases/api/useCase'
import {
  useApplyDirectionTemplate,
  useCaseLeg,
  useReplaceLegPointsOf,
} from '@/features/cases/api/useCaseLeg'
import type { LegDraft } from '@/features/cases/components/CaseRecordSheets'
import {
  LegFinance,
  UnitsCard,
  chainWithReachedCount,
  legIdentityHidden,
} from '@/features/cases/components/legs'
import { AddUnitKind } from '@/features/cases/components/legs/AddUnitKind'
import {
  chainEnds,
  chainFromEnds,
  chainFromStored,
  chainStep,
  directionPayloadOf,
  effectiveTemplateName,
  templateBlock,
  type ChainDraft,
  type ChainStep,
  type TemplateNameDraft,
} from '@/features/cases/components/legs/legChain'
import { legCarriers } from '@/features/cases/components/legs/legCarriers'
import {
  LegParamsDrawer,
  type LegParamsTab,
  type LegParamsValue,
} from '@/features/cases/components/legs/LegParamsDrawer'
import {
  MAX_LEG_UNITS,
  composeLegRate,
  composeRates,
  emptyLegRate,
  legRateProblem,
  markRatesWritten,
  mergeAfterConflict,
  presentKinds,
  reconcileRows,
  rowProblems,
  rowWritesRate,
  seedRows,
  splitByUnit,
  unitsPayload,
  unitsTouched,
  type LegRateDraft,
  type NamedUnit,
  type RateLine,
  type RowDefaults,
  type UnitRow,
} from '@/features/cases/components/legs/legRows'
import { LegSaveBar } from '@/features/cases/components/legs/LegSaveBar'
import { LegSummary, type StripPoint } from '@/features/cases/components/legs/LegSummary'
import { LegWholeCard } from '@/features/cases/components/legs/LegWholeCard'
import { UNSET_PARENT, heldParent, isParentLine, truckDetail } from '@/features/cases/components/legs/truckDetail'
import { shownUnitKinds } from '@/features/cases/components/legs/unitKinds'
import { useTableFits } from '@/features/cases/components/legs/useTableFits'
import { refusalMessage } from '@/features/cases/lib/refusal'
import { counterpartyRights } from '@/features/counterparties/counterpartyList'
import { useTransportRefs } from '@/features/directories/api/useDirectory'
import { CarrierPaymentSheet } from '@/features/finance/components/CarrierPaymentSheet'
import { useCreateDirection, type DirectionPayload } from '@/features/settings/api/useDirectionTemplates'
import { ApiError } from '@/lib/api'
import { CAP, can } from '@/lib/can'
import { formatMoney } from '@/lib/format'
import { UNIT_KINDS, unitLabel } from '@/lib/legUnits'
import { fieldErrors } from '@/lib/problems'
import type { CaseDetail, CaseLeg, ExpenseLine, LegUnit, Money, UnitKind } from '@/types/domain'

/* One leg on its own page, at `/cases/:caseId/legs/new` and
 * `/cases/:caseId/legs/:legId` — ONE surface for creating a leg and for working
 * on it, including the planned leg quote conversion creates with no units
 * (S-10, design record A3).
 *
 * ── The page is the draft, and one bar writes it ────────────────────────────
 *
 * The leg's route, its fields, its units with their carriers and its rates are
 * one draft held here. «Параметры плеча» edits the leg's own fields in a drawer
 * whose «Готово» writes nothing; the unit cards («Фуры», «Контейнеры»,
 * «Коносаменты»… — one per kind the mode implies or the leg holds, S-18b) edit
 * the units inline; the sticky bar at the bottom is the only thing that
 * writes, and one press does all of it — in an order that is not a choice:
 *
 *   1. the LEG — POST on `legs/new`, otherwise a version-checked PUT that
 *      carries only what this draft changed (`legUpdatePayload`);
 *   2. the CHAIN — `apply-template`, `PUT …/points`, or nothing
 *      (`legChain.chainStep`), on a leg with no stored points; on one with,
 *      only a template the drawer chose to replace it;
 *   3. the RATES — one batch of planned cost lines, keyed to the unit ids the
 *      leg write answered with (two writes, not one atomic endpoint: the units
 *      are casefile's and the money is finance's, AD-3) — and, since S-18a,
 *      the leg rate of «Плечо целиком» in the same batch, a line with the leg
 *      and no unit, written once while the leg has no leg-level plan;
 *   4. the RE-FILING — S-12's `detailOf` / `standsAlone` PATCHes, when the
 *      «Ставки детализируют» answer moved;
 *   5. the TEMPLATE — «Сохранить как шаблон», last and non-critical: a refused
 *      template never undoes a saved leg.
 *
 * ── What survives a step that fails ─────────────────────────────────────────
 *
 * Every step after the leg write is HELD in this page's state before it is
 * sent and dropped only when it lands, so a failure is reported as what it is —
 * «the leg is saved, 3 rates are not» — and «Повторить» re-sends THOSE
 * steps, never re-derived ones. That is also why the create's
 * `navigate(replace)` matters: `legs/new` is this same route with the literal
 * in the param, so the navigation is a PARAM change, this component stays
 * mounted, and the held steps cross the `new` → id switch with it.
 *
 * Because a held step is re-sent as itself, the screen shows it as such: a
 * unit whose rate line is held shows that amount and currency read-only, not
 * an input whose edit the next press would not send, and offers neither a
 * remove control nor an editable number; «Ставки детализируют» shows the held
 * lines' own answer read-only, and a rate typed beside them is filed under
 * that same answer. Every control that could change what a held step says is
 * frozen. Held lines that carry NO answer waive nothing: while the plan offers
 * a line to detail, the press is refused until «Отмена» discards them — and
 * «Повторить» is that same press, refused by the same `blockedReason` (on the
 * button, and as the first guard in `save()`).
 *
 * The rates batch is all-or-none, so before it is sent EVERY line — held or
 * composed from the rows — is checked against the unit list the server last
 * answered with (the leg write's answer; on a retry with no leg write, a fresh
 * read of the leg). A line whose unit is gone is not sent, and the report
 * names it by kind and number. «Отмена» discards the draft and every held step composed
 * from it.
 *
 * ── After a write, the rows are reconciled, not re-seeded ───────────────────
 *
 * The leg write's own answer is the truth until the refetch lands (the writes
 * only invalidate): each row takes its unit id by `unitKey(kind, number)` and
 * keeps what was typed; a rate whose batch landed is marked and never sent
 * again; the unit-list version is the answer's. That holds for a write that
 * SENT the units (the create, a PUT with `units`). A PUT that left them out
 * says nothing about them, and its answer may carry a colleague's list: the
 * rows keep their own base and version, so the next units PUT is refused 409
 * and names a unit the colleague removed rather than re-add it. A clean grid then
 * follows the newer answer through the re-seed. A second press after a full
 * save sends no POST and no batch.
 *
 * ── A colleague's save in between ──────────────────────────────────────────
 *
 * The PUT carries the version of the unit list the rows were SEEDED from —
 * the latest answer while the rows are clean, and kept while they are being
 * edited, so a refetch landing mid-edit cannot launder a stale list into a
 * fresh token. A stale one is 409 `leg-units-changed`: the page re-reads the
 * leg, lays this reader's unsaved rows and edits over the stored units, names
 * the units that vanished, and asks to press again.
 *
 * ── Which cards ─────────────────────────────────────────────────────────────
 *
 * The draft's mode decides the default cards (`unitKinds.defaultUnitKinds`,
 * the catalogue code of the draft's mode row, else the served
 * `transportModeCode` — never the lossy `CaseLeg.mode`), so a new leg switched
 * to «Море» shows «Контейнеры» and «Коносаменты» before its first save. A kind
 * holding a unit, saved or typed, is always shown; «+ Другой вид единицы» adds
 * any other kind's card.
 *
 * ── Nothing on this page decides where the cargo is ─────────────────────────
 *
 * `currentPointId` and `nextPointDueAt` are SERVED; the summary marks them and
 * compares one timestamp with the clock. Re-deriving the position here would be
 * the second definition of it that `legnow` exists to prevent.
 */

export function CaseLegPage() {
  const { caseId = '', legId = '' } = useParams()
  const navigate = useNavigate()
  const drafting = legId === 'new'

  /* The id this page created, so the desk is not swapped for a skeleton while
     the new leg's first read is in flight: the desk holds the create's answer
     and every step still to send. */
  const [created, setCreated] = useState<string | null>(null)

  const caseRead = useCase(caseId)
  const legRead = useCaseLeg(caseId, drafting ? '' : legId)

  /* The draft is SEEDED from the case on `legs/new` (QW-8), once — so it waits
     for a read of the case as it is now, not a cached copy from before somebody
     edited the last leg on another tab. */
  const caseFresh = caseRead.isFetchedAfterMount || !caseRead.isStale
  const continuing = created !== null && created === legId

  const body = drafting ? (
    caseRead.isError ? (
      <ErrorState error={caseRead.error} onRetry={() => void caseRead.refetch()} />
    ) : caseRead.data === undefined || !caseFresh ? (
      <PageSkeleton />
    ) : null
  ) : continuing || legRead.data ? null : legRead.isError ? (
    <ErrorState error={legRead.error} onRetry={() => void legRead.refetch()} />
  ) : (
    <PageSkeleton />
  )

  /* The way back is on every branch, the failures included: a page reached by
     URL that answers 403 with nothing but an error box is a dead end. */
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <BackToCase caseId={caseId} />
      {body ?? (
        <LegDesk
          caseId={caseId}
          record={caseRead.data}
          leg={legRead.data}
          legReadAt={legRead.dataUpdatedAt}
          refetchLeg={async () => (await legRead.refetch()).data}
          onRetryMoney={() => void legRead.refetch()}
          onCreated={(id) => {
            setCreated(id)
            /* `replace`: Back must not return to a create form already used —
               pressing it would offer to make a SECOND leg. */
            void navigate(`/cases/${caseId}/legs/${id}`, { replace: true })
          }}
          onLeave={() => void navigate(`/cases/${caseId}?section=legs`)}
        />
      )}
    </div>
  )
}

function PageSkeleton() {
  return (
    <>
      <Skeleton className="h-7 w-72" />
      <Skeleton className="h-80 rounded-[var(--radius-DEFAULT)]" />
    </>
  )
}

/** The link back to the case's legs — where the leg was opened from. */
function BackToCase({ caseId }: { caseId: string }) {
  const { t } = useTranslation()
  return (
    <Link
      to={`/cases/${caseId}?section=legs`}
      className="text-text-link hover:text-text-link-hover flex w-fit items-center gap-1.5 text-[13px]"
    >
      <ArrowLeft size={13} aria-hidden />
      {t('cases.legPage.back')}
    </Link>
  )
}

/** The steps of a press still to be sent, held across the `new` → id switch
 *  and across a failure, and retried as themselves. */
interface HeldSteps {
  legId: string
  chain?: ChainStep
  rates?: RateLine[]
  refile?: { lineIds: string[]; detailOf: string | null; standsAlone: boolean }
  template?: DirectionPayload
}

type Step = 'leg' | 'chain' | 'rates' | 'refile' | 'template'

/** The rows — every kind's, one list — and the stored unit list they were
 *  seeded from, with its version, which is what the PUT sends back. */
interface RowsState {
  rows: UnitRow[]
  base: LegUnit[]
  version: string
}

/** Statuses on which the server refuses every route write (FR-25:
 *  `Case.Editable`, the terminal states). */
const ROUTE_LOCKED = new Set(['financially-closed', 'cancelled'])

function LegDesk({
  caseId,
  record,
  leg,
  legReadAt,
  refetchLeg,
  onRetryMoney,
  onCreated,
  onLeave,
}: {
  caseId: string
  record?: CaseDetail
  /** The composed read — absent on `legs/new`, and for a moment after the
   *  create, when the create's own answer stands in for it. */
  leg?: CaseLeg
  legReadAt: number
  refetchLeg: () => Promise<CaseLeg | undefined>
  onRetryMoney: () => void
  onCreated: (legId: string) => void
  onLeave: () => void
}) {
  const { t } = useTranslation()
  const { data: user, isSuccess: sessionKnown } = useSession()

  const addLeg = useAddLeg(caseId)
  const updateLeg = useUpdateLeg(caseId)
  const applyTemplate = useApplyDirectionTemplate(caseId)
  const replaceChain = useReplaceLegPointsOf(caseId)
  /* The overdue line's «Отметить прибытие» — an immediate write of its own,
     apart from the save's chain step so each refusal is said where it came
     from. */
  const markPoints = useReplaceLegPointsOf(caseId)
  const batch = useRecordExpenseBatch(caseId)
  const setDetailOf = useSetExpenseDetailOf(caseId)
  const createDirection = useCreateDirection()

  /* The latest server answer: a write's, until the read that follows it lands.
     The writes answer the plain leg DTO (no money), so the composed read stays
     the source of everything money-shaped. */
  const [answer, setAnswer] = useState<{ leg: CaseLeg; at: number } | null>(null)
  const latest = answer && (!leg || answer.at > legReadAt) ? answer.leg : leg
  const composed = leg ?? undefined
  const isNew = latest === undefined
  const storedPoints = latest?.points ?? []
  const hasChain = storedPoints.length > 0

  /* ── The draft ─────────────────────────────────────────────────────────── */

  const [seed, setSeed] = useState<LegDraft | null>(null)
  if (isNew && seed === null && record) setSeed(legDraftForCase(record))

  const [edits, setEdits] = useState<LegEdits>({})
  const [chain, setChain] = useState<ChainDraft | null>(null)
  const [templateApply, setTemplateApply] = useState('')
  const [saveAsTemplate, setSaveAsTemplate] = useState<TemplateNameDraft | null>(null)
  /* «Новые строки:»'s rate and currency, per card: a container's 900 USD is
     not a B/L's default. */
  const [rateDefaults, setRateDefaults] = useState<Partial<Record<UnitKind, { rate: string; currency: string }>>>({})
  /* The kinds «+ Другой вид единицы» added beyond the mode's defaults. */
  const [addedKinds, setAddedKinds] = useState<UnitKind[]>([])
  const [parentChoice, setParentChoice] = useState<string | null>(null)
  /* «Плечо целиком»'s rate (S-18a): the leg-level twin of a row's rate. */
  const [legRate, setLegRate] = useState<LegRateDraft>(emptyLegRate)
  /* «Оплатить перевозчику» from the summary — the sheet with no unit implied. */
  const [payingCarrier, setPayingCarrier] = useState(false)

  const base: LegDraft | null = latest ? legDraftFrom(latest) : seed
  const view: LegDraft | null = base ? { ...base, ...edits } : null

  /* THE VERSION TRAVELS WITH THE BASE. `rowsState.version` is the token of the
     stored unit list the rows were seeded or reconciled from — never the
     newest token seen. A clean grid follows the latest answer (it re-seeds,
     below, and takes that answer's version); an edited grid keeps its base's
     version, so a refetch landing mid-edit cannot launder a stale list into a
     fresh token, and the PUT is refused with 409 `leg-units-changed` instead
     of reverting a colleague's units. */
  const [rowsState, setRowsState] = useState<RowsState | null>(null)
  const [saving, setSaving] = useState(false)
  if (rowsState === null && (isNew || latest)) {
    setRowsState({
      rows: seedRows(latest?.units ?? []),
      base: latest?.units ?? [],
      version: latest?.unitsVersion ?? '',
    })
  }
  const rows = rowsState?.rows ?? []

  /* ── What this reader may do ───────────────────────────────────────────── */

  const routeLocked = record !== undefined && ROUTE_LOCKED.has(record.status)
  /* Refused the leg's carrier and drivers (no `counterparty.read`, AD-6): the
     whole leg is read-only for this reader, not only its units — the drawer,
     the leg rate and the save bar too, so nothing takes input that no press
     could send (#133). A new leg carries no markers yet, so it stays theirs
     to create. Read off the payload (`legIdentityHidden`), never a role. */
  const identityHidden = latest !== undefined && legIdentityHidden(latest)
  /* The route edit alone — what the summary's «next point» act needs: it
     writes only the chain's reached marks, never identity. */
  const routeReadOnly = !can(user, CAP.caseRouteEdit) || routeLocked
  const readOnly = routeReadOnly || identityHidden
  const readOnlyReason = !readOnly
    ? undefined
    : routeLocked
      ? t('cases.legSave.readOnlyClosed')
      : routeReadOnly
        ? t('cases.legSave.readOnlyNoRight')
        : t('cases.legs.identityHidden')
  const showIdentity = user?.seesAgentIdentity === true
  const moneyForbidden = isNew || !composed
    ? record?.moneyUnavailable === 'forbidden'
    : composed.moneyUnavailable === 'forbidden'
  const canRate = can(user, CAP.financeExpenseRecord) && !moneyForbidden && !readOnly
  /* «Оплатить фуру» books its payee and its debts to DIRECTORY counterparties
     (S-22d), and the server refuses it (403 `held_by: counterparty.read`) to a
     reader who cannot see them — so it is not offered. The same gate reaches
     the unit rows through `canPay`. */
  const canPay =
    can(user, CAP.financePaymentCarrier) &&
    counterpartyRights(user).read &&
    composed?.money !== undefined
  const canManageTemplates = can(user, CAP.routeTemplateManage)

  /* The drawer opens by itself on a new leg, once — and only for a reader who
     may write it; on an existing leg it opens only when asked.

     `seeded` is the view the drawer was opened on — what its own draft seeds
     from, once per open. «Готово» counts as the reader's edit only a key that
     differs from THAT, never from the base as it is at «Готово»: a refetch
     landing while the drawer is open (the sweep moved the leg to `arrived`)
     would otherwise turn every field the server changed into an edit, and the
     PUT would write the old status and position back (AC #2). */
  const [drawer, setDrawer] = useState<{ open: boolean; tab: LegParamsTab; seeded: LegDraft | null }>({
    open: false,
    tab: 'route',
    seeded: null,
  })
  const [autoOpened, setAutoOpened] = useState(false)
  if (isNew && !autoOpened && sessionKnown) {
    setAutoOpened(true)
    if (!readOnly) setDrawer({ open: true, tab: 'route', seeded: view })
  }
  /* The auto-open can come before the seed exists; the drawer mounts only
     with the view, and this snapshots it in that same render. */
  if (drawer.open && drawer.seeded === null && view) setDrawer({ ...drawer, seeded: view })
  const openDrawer = (tab: LegParamsTab) => setDrawer({ open: true, tab, seeded: view })
  const closeDrawer = () => setDrawer({ ...drawer, open: false, seeded: null })

  /* ── Money read ────────────────────────────────────────────────────────── */

  const expenseLines = useCaseExpenseLines(moneyForbidden ? '' : caseId)
  const lines = expenseLines.data ?? []
  const legId = latest?.id ?? ''
  const planned = plannedUnits(lines, legId)
  const moneyHeld = new Set([
    ...lines.flatMap((line) => (line.unitId ? [line.unitId] : [])),
    ...(composed?.money?.perUnit ?? [])
      .filter((row) => [row.agreed, row.actual, row.paid].some((money) => Number(money.amount) !== 0))
      .map((row) => row.unitId),
  ])
  const bookedRate = (unitId: string): Money | undefined => {
    const plans = lines.filter((line) => line.legId === legId && line.unitId === unitId && !line.unplanned)
    if (plans.length === 1) return plans[0]!.planned
    return composed?.money?.perUnit.find((row) => row.unitId === unitId)?.agreed
  }
  /* «Остаток» per unit (S-18b): served, read-only, never computed here. */
  const remaining = (unitId: string): Money | undefined =>
    composed?.money?.perUnit.find((row) => row.unitId === unitId)?.remaining

  /* The leg's leg-level plans (S-18a): the leg rate is typed only while there
     is none — S-10's rule, the one the `planned` set answers for a unit. */
  const legPlans = lines.filter(
    (line) => legId !== '' && line.legId === legId && line.unitId === undefined && !line.unplanned,
  )
  const hasLegPlan = legPlans.length > 0
  const legRateWrites =
    canRate && composeLegRate({ draft: legRate, legId, hasLegPlan, item: '', link: {} }) !== null
  const hasNewRates = canRate && (rows.some((row) => rowWritesRate(row, planned)) || legRateWrites)
  const detail = truckDetail({ lines, legId, parentChoice, hasNewRates })
  const parentMoved = canRate && detail.parentMoved

  /* ── Save state ────────────────────────────────────────────────────────── */

  const [held, setHeld] = useState<HeldSteps | null>(null)
  const [failure, setFailure] = useState<{ step: Step; error: unknown } | null>(null)
  const [conflict, setConflict] = useState<{ vanished: NamedUnit[] } | null>(null)
  /* The units whose rate the last press did not send: they were not on the
     leg the server answered with (`splitByUnit`). Said, never dropped
     silently. */
  const [unsent, setUnsent] = useState<NamedUnit[]>([])
  /* The row keys the last leg write sent, in payload order: a served
     `units.<i>.<field>` names its row by that index (AC #14). */
  const [sentRowKeys, setSentRowKeys] = useState<string[]>([])
  /* The «Ставки детализируют» answer held rate lines carry. While there is
     one, the select is frozen to it: «Повторить» re-sends those lines as they
     are, and a select that moved would only say what they do not. */
  const heldAnswer = heldParent(held?.rates?.map(({ line }) => line))
  /* Only an ANSWER waives the question: a parent, or the stored «не
     детализирует». Held lines composed with no answer settle nothing — while
     the plan offers a line they could detail, «Сохранить» and «Повторить»
     stay refused (both read `blockedReason`) and «Отмена» (which discards
     them) is the way to answer. */
  const heldAnswered = heldAnswer !== undefined && heldAnswer !== UNSET_PARENT

  const problems = new Map(
    rows.map((row) => [row.key, rowProblems(row, canRate && !(row.id !== '' && planned.has(row.id)))]),
  )
  const badRates = [...problems.values()].filter((problem) => problem.rate).length
  const badWeights = [...problems.values()].filter((problem) => problem.weight).length
  const heldLegRate = held?.rates?.find(({ line }) => line.unitId === undefined)?.line.amount
  const badLegRate = canRate && !hasLegPlan && heldLegRate === undefined && legRateProblem(legRate)

  const fieldsDirty = Object.keys(edits).length > 0
  const unitsDirty = rowsState
    ? isNew
      ? unitsPayload(rows).length > 0
      : unitsTouched(rows, rowsState.base)
    : false
  const chainDirty = isNew ? false : chain !== null || templateApply !== ''
  const templateOn =
    canManageTemplates &&
    saveAsTemplate !== null &&
    templateBlock(chain ? chain.points : chainFromStored(storedPoints)) === null
  const dirty =
    isNew || fieldsDirty || unitsDirty || chainDirty || hasNewRates || parentMoved || templateOn

  /* A clean row set follows the server: a refetch carrying a new unit list
     (a colleague's save), or the answer of a leg write that sent no units,
     re-seeds rows nobody here has touched. Rows being edited keep their base
     — and its version — so the PUT can be refused rather than revert the
     colleague's units. */
  if (
    rowsState !== null &&
    latest !== undefined &&
    !saving &&
    latest.unitsVersion !== rowsState.version &&
    !unitsTouched(rows, rowsState.base) &&
    !rows.some((row) => !row.pristine && row.rate.trim() !== '' && !row.rateWritten) &&
    /* A new row with no number yet — only a trailer, a driver typed — is not
       in the units payload, so `unitsTouched` cannot see it; re-seeding would
       drop it silently. */
    !rows.some((row) => !row.pristine && row.id === '')
  ) {
    setRowsState({ rows: seedRows(latest.units), base: latest.units, version: latest.unitsVersion })
  }

  /* The catalogue code of the draft's mode — what decides the default cards.
     The draft's own row first, so a new leg switched to «Море» switches its
     cards before the first save; the served code while the catalogue loads. */
  const { data: modes } = useTransportRefs('mode', true)

  if (!view || !rowsState) return <PageSkeleton />

  const modeCode =
    modes?.find((mode) => mode.id === view.transportModeId)?.code ?? latest?.transportModeCode
  const kinds = shownUnitKinds({ code: modeCode, present: presentKinds(rows), added: addedKinds })

  const missing: string[] = []
  if (view.origin.trim() === '') missing.push(t('cases.legSave.missingOrigin'))
  if (view.destination.trim() === '') missing.push(t('cases.legSave.missingDestination'))
  if (view.transportModeId === '') missing.push(t('cases.legSave.missingMode'))
  const missingTab: LegParamsTab =
    view.origin.trim() === '' || view.destination.trim() === '' ? 'route' : 'transport'

  /* A reader refused the identity gets no bar at all (`readOnly`); the arm
     stays because `save()` reads this too, so no other path can send it. */
  const blockedReason = identityHidden
    ? t('cases.legs.identityHidden')
    : missing.length > 0
      ? t('cases.legSave.missing', { fields: missing.join(', ') })
      : badRates > 0
        ? t('cases.legs.vehiclesTable.rateInvalid', { count: badRates })
        : badWeights > 0
          ? t('cases.legs.vehiclesTable.weightInvalid', { count: badWeights })
          : badLegRate
            ? t('cases.legWhole.rateInvalid')
            : unitsPayload(rows).length > MAX_LEG_UNITS
              ? t('cases.legSave.overCap', { max: MAX_LEG_UNITS })
              : detail.mustChoose && canRate && !heldAnswered
                ? heldAnswer === UNSET_PARENT
                  ? t('cases.legs.vehiclesTable.detailOfRequiredHeld')
                  : t('cases.legs.vehiclesTable.detailOfRequired')
                : hasNewRates && expenseLines.isPending
                  ? t('cases.legs.vehiclesTable.planLinesLoading')
                  : hasNewRates && expenseLines.isError
                    ? t('cases.legs.vehiclesTable.planLinesFailed')
                    : !dirty && held === null
                      ? t('cases.legSave.nothingToSave')
                      : undefined

  /* ── The press ─────────────────────────────────────────────────────────── */

  async function save() {
    /* One guard for both presses: «Повторить» re-sends the held steps through
       this same function, so a refusal the save bar shows refuses it too. */
    if (!view || !rowsState || blockedReason !== undefined) return
    setSaving(true)
    setFailure(null)
    setConflict(null)
    setUnsent([])

    let stage: Step = 'leg'
    let current = latest
    /* Whether this press wrote the leg: its answer is then the unit list the
       rates are checked against, and no re-read is needed. */
    let legWritten = false
    let currentRows = rowsState.rows
    /* Derived NOW, before the leg write clears the draft, and merged under any
       step a failed press is still holding — that step is retried as itself. */
    const linkOf = (parent: string): { detailOf: string } | { standsAlone: true } | Record<string, never> =>
      isParentLine(parent) ? { detailOf: parent } : parent === '' ? { standsAlone: true } : {}
    /* While rate lines are held the select is frozen to THEIR answer, so a
       rate typed beside them is filed under the same one. */
    const link = linkOf(heldAnswer ?? detail.chosenParent)
    const derivedChain: ChainStep | undefined = hasChain
      ? templateApply !== ''
        ? { kind: 'apply', templateId: templateApply }
        : undefined
      : (chainStep(chain) ?? undefined)
    const templatePoints = chain ? chain.points : chainFromStored(storedPoints)
    const derivedTemplate =
      templateOn && saveAsTemplate
        ? directionPayloadOf(effectiveTemplateName(saveAsTemplate, templatePoints), templatePoints)
        : undefined
    const derivedRefile = parentMoved
      ? {
          lineIds: detail.legPlans.map((line) => line.id),
          detailOf: detail.linkTo,
          standsAlone: detail.standsAlone,
        }
      : undefined
    const retryingRates = (held?.rates ?? []).length > 0

    const steps: Omit<HeldSteps, 'legId'> = {
      chain: held?.chain ?? derivedChain,
      rates: held?.rates,
      refile: derivedRefile ?? held?.refile,
      template: held?.template ?? derivedTemplate,
    }
    const hold = (id: string) => setHeld({ legId: id, ...steps })

    const landed = (saved: CaseLeg) => {
      current = saved
      setAnswer({ leg: saved, at: Date.now() })
    }

    try {
      /* 1 — the leg. */
      if (isNew) {
        setSentRowKeys(sentKeysOf(currentRows))
        const created = await addLeg.mutateAsync({
          ...toLegPayload(view),
          units: unitsPayload(currentRows),
        })
        landed(created)
        legWritten = true
        currentRows = reconcileRows(currentRows, created.units)
        setRowsState({ rows: currentRows, base: created.units, version: created.unitsVersion })
        setEdits({})
        setChain(null)
        hold(created.id)
        onCreated(created.id)
      } else if (current && (fieldsDirty || unitsDirty)) {
        if (unitsDirty) setSentRowKeys(sentKeysOf(currentRows))
        const updated = await updateLeg.mutateAsync({
          legId: current.id,
          payload: legUpdatePayload({
            live: current,
            edits,
            units: unitsDirty
              ? { list: unitsPayload(currentRows), version: rowsState.version }
              : undefined,
          }),
        })
        landed(updated)
        legWritten = true
        /* Only a write that SENT the units makes its answer the rows' base.
           One that left them out says nothing about them: its answer may carry
           a colleague's list (a unit removed, a fresh version), and adopting
           it would turn this reader's stale rows into a payload the next PUT
           sends under the colleague's token — re-adding the removed unit
           with no 409. The rows keep their base and its version; a clean grid
           still follows the latest answer through the render-time re-seed. */
        if (unitsDirty) {
          currentRows = reconcileRows(currentRows, updated.units)
          setRowsState({ rows: currentRows, base: updated.units, version: updated.unitsVersion })
        }
        setEdits({})
        hold(updated.id)
      }
      if (!current) return
      const id = current.id
      hold(id)

      /* 2 — the chain. */
      if (steps.chain) {
        stage = 'chain'
        const step = steps.chain
        const chained =
          step.kind === 'apply'
            ? await applyTemplate.mutateAsync({ legId: id, templateId: step.templateId })
            : await replaceChain.mutateAsync({ legId: id, payload: step.payload })
        landed(chained)
        steps.chain = undefined
        setChain(null)
        setTemplateApply('')
        hold(id)
      }

      /* 3 — the rates. Held lines first; before re-sending them, any whose
         unit now HAS a per-unit plan is dropped: a batch that committed and
         whose answer was lost is not booked twice. With no leg write in this
         press the leg is read again too, so the unit check below sees the
         units as they are now, not as the held lines were composed. */
      if (canRate) {
        stage = 'rates'
        let plannedNow = planned
        let legPlannedNow = hasLegPlan
        if (retryingRates && steps.rates) {
          const [fresh, freshLeg] = await Promise.all([
            expenseLines.refetch(),
            legWritten ? undefined : refetchLeg(),
          ])
          if (freshLeg) current = freshLeg
          plannedNow = plannedUnits(fresh.data ?? [], id)
          legPlannedNow = (fresh.data ?? []).some(
            (line) => line.legId === id && line.unitId === undefined && !line.unplanned,
          )
          /* A held leg rate is dropped like a held unit rate: once the leg
             HAS a leg-level plan, the batch that wrote it landed. */
          steps.rates = steps.rates.filter(({ line }) =>
            line.unitId === undefined ? !legPlannedNow : !plannedNow.has(line.unitId),
          )
        }
        const heldRates = steps.rates ?? []
        const composedRates = composeRates({
          rows: currentRows,
          units: current.units,
          legId: id,
          planned: plannedNow,
          /* STORED wording (`item` on the cost line): a truck's rate keeps
             «Ставка фуры {{plate}}» verbatim, every other kind has its own. */
          item: (unit) =>
            unit.kind === 'truck'
              ? t('cases.legs.vehiclesTable.rateItem', { plate: unit.number })
              : t(`cases.legs.units.rateItem.${unit.kind}` as 'cases.legs.units.rateItem.container', {
                  number: unit.number,
                }),
          link,
        }).filter(({ line }) => !heldRates.some((other) => other.line.unitId === line.unitId))
        /* The leg rate rides the same batch, under the same answer. */
        const legLine = heldRates.some(({ line }) => line.unitId === undefined)
          ? null
          : composeLegRate({
              draft: legRate,
              legId: id,
              hasLegPlan: legPlannedNow,
              item: t('cases.legWhole.rateItem'),
              link,
            })
        if (legLine) composedRates.push(legLine)
        /* The batch is all-or-none: one line naming a unit the leg no longer
           has (a colleague removed it, a 409 reconcile dropped it) would
           refuse every other rate with it, on every retry. EVERY line is
           checked — a composed one too, since a row's id can be stale — and
           a line whose unit is gone is not sent, and is named. */
        const { send, gone } = splitByUnit([...heldRates, ...composedRates], current.units)
        if (gone.length > 0) setUnsent(gone.flatMap(({ unit }) => (unit ? [unit] : [])))
        steps.rates = send
        if (send.length > 0) {
          hold(id)
          const written = new Set(send.flatMap(({ line }) => (line.unitId ? [line.unitId] : [])))
          await batch.mutateAsync(send.map(({ line }) => line))
          currentRows = markRatesWritten(currentRows, written)
          setRowsState((state) => (state ? { ...state, rows: markRatesWritten(state.rows, written) } : state))
          if (send.some(({ line }) => line.unitId === undefined)) {
            setLegRate((draft) => ({ ...draft, written: true }))
          }
        }
        steps.rates = undefined
        hold(id)
      }

      /* 4 — the re-filing. */
      if (steps.refile) {
        stage = 'refile'
        const refile = steps.refile
        await Promise.all(
          refile.lineIds.map((lineId) =>
            setDetailOf.mutateAsync({
              lineId,
              detailOf: refile.detailOf,
              ...(refile.standsAlone ? { standsAlone: true } : {}),
            }),
          ),
        )
        steps.refile = undefined
        setParentChoice(null)
        hold(id)
      }

      /* 5 — the template, last: its refusal never undoes the leg. */
      if (steps.template) {
        stage = 'template'
        await createDirection.mutateAsync(steps.template)
        steps.template = undefined
        setSaveAsTemplate(null)
      }

      setHeld(null)
    } catch (error) {
      setFailure({ step: stage, error })
      if (stage === 'leg' && error instanceof ApiError && error.code === 'leg-units-changed') {
        const fresh = await refetchLeg()
        if (fresh) {
          const merged = mergeAfterConflict(currentRows, rowsState.base, fresh.units)
          setRowsState({ rows: merged.rows, base: fresh.units, version: fresh.unitsVersion })
          setConflict({ vanished: merged.vanished })
        }
      }
    } finally {
      setSaving(false)
    }
  }

  /* «Отмена» discards the draft; with nothing to discard it is the way back,
     as on `legs/new`. The steps a failed press is holding go with it: every
     one was composed from this draft — the rates from the rows, the chain and
     the template from the drawer, the re-filing from «Ставки детализируют» —
     and a step the reader discarded must not be what the next press sends. */
  const cancel = () => {
    if (isNew || (!dirty && held === null)) {
      onLeave()
      return
    }
    setHeld(null)
    setEdits({})
    setChain(null)
    setTemplateApply('')
    setSaveAsTemplate(null)
    setParentChoice(null)
    setLegRate(emptyLegRate())
    setFailure(null)
    setConflict(null)
    setUnsent([])
    setSentRowKeys([])
    if (latest) {
      setRowsState({ rows: seedRows(latest.units), base: latest.units, version: latest.unitsVersion })
    }
  }

  /* ── The drawer's copy of the draft ────────────────────────────────────── */

  const drawerValue: LegParamsValue = {
    fields: view,
    chain: hasChain ? null : (chain ?? chainFromEnds(view)),
    templateApply,
    saveAsTemplate,
  }

  const applyParams = (next: LegParamsValue) => {
    if (!base || !view) return
    /* A key is the reader's edit when the drawer's result differs from what
       the drawer was SEEDED with; a key the reader did not touch keeps what
       the draft held before. The current base only drops an edit that now
       equals the server's value — it never makes one. */
    const seeded = drawer.seeded ?? view
    const same = (a: unknown, b: unknown) => JSON.stringify(a ?? '') === JSON.stringify(b ?? '')
    const nextEdits: Record<string, unknown> = { ...edits }
    for (const key of Object.keys(next.fields) as Array<keyof LegDraft>) {
      if (!same(next.fields[key], seeded[key])) nextEdits[key] = next.fields[key]
      if (key in nextEdits && same(nextEdits[key], base[key])) delete nextEdits[key]
    }
    setEdits(nextEdits as LegEdits)
    if (!hasChain && next.chain) {
      const untouched =
        next.chain.templateId === '' &&
        next.chain.points.length === 2 &&
        next.chain.points.every((point, index) => point.days.trim() === '' || index === 0) &&
        JSON.stringify(chainEnds(next.chain)) ===
          JSON.stringify(chainEnds(chainFromEnds(base)))
      const nextChain = untouched ? null : next.chain
      if (JSON.stringify(nextChain) !== JSON.stringify(chain) && held?.chain) {
        // A chain drafted again replaces the step a failed press held.
        setHeld({ ...held, chain: undefined })
      }
      setChain(nextChain)
    }
    setTemplateApply(next.templateApply)
    setSaveAsTemplate(next.saveAsTemplate)
    closeDrawer()
  }

  /* ── Rendering ─────────────────────────────────────────────────────────── */

  const rowDefaults = (kind: UnitKind): RowDefaults => ({
    carrierId: view.carrierId,
    carrierName: view.carrierName,
    rate: rateDefaults[kind]?.rate ?? '',
    currency: rateDefaults[kind]?.currency ?? 'USD',
  })
  const changeDefaults = (kind: UnitKind) => (next: RowDefaults) => {
    setRateDefaults({ ...rateDefaults, [kind]: { rate: next.rate, currency: next.currency } })
    if (next.carrierId !== view.carrierId || next.carrierName !== view.carrierName) {
      const carrier = { carrierId: next.carrierId, carrierName: next.carrierName }
      const sameAsBase = base && carrier.carrierId === base.carrierId && carrier.carrierName === base.carrierName
      const { carrierId: _id, carrierName: _name, ...rest } = edits
      setEdits(sameAsBase ? rest : { ...rest, ...carrier })
    }
  }

  const carriers = legCarriers({
    leg: { carrierId: view.carrierId, carrierName: view.carrierName },
    units: rows.filter((row) => !row.pristine && row.number.trim() !== ''),
    seesAgentIdentity: showIdentity && !identityHidden,
  })

  /* A served `units.<i>.<field>` refusal of the last leg write, under the row
     it names (AC #14): the index is the row's place in the list that write
     sent. */
  const rowErrors = new Map<string, Array<{ field: string; code: string }>>()
  if (failure?.step === 'leg') {
    for (const [path, codes] of Object.entries(fieldErrors(failure.error))) {
      const match = /^units\.(\d+)\.(\w+)$/.exec(path)
      const key = match ? sentRowKeys[Number(match[1])] : undefined
      if (!match || key === undefined || codes[0] === undefined) continue
      rowErrors.set(key, [...(rowErrors.get(key) ?? []), { field: match[2]!, code: codes[0] }])
    }
  }

  const stripPoints: StripPoint[] = hasChain
    ? storedPoints.map((point) => ({
        key: point.id,
        name: point.name,
        country: point.country || undefined,
        reached: point.reachedAt !== undefined,
      }))
    : (chain?.points ?? []).length > 2 || chain?.templateId
      ? (chain?.points ?? []).map((point) => ({
          key: point.key,
          name: point.name,
          country: point.country || undefined,
        }))
      : []
  const automated = storedPoints.some((point) => point.plannedDaysFromPrevious !== undefined)

  const currentIndex = storedPoints.findIndex((point) => point.id === latest?.currentPointId)

  const rateMissingCurrency =
    failure?.step === 'rates' &&
    failure.error instanceof ApiError &&
    failure.error.code === 'rate-missing' &&
    typeof failure.error.problem.currency === 'string'
      ? failure.error.problem.currency
      : undefined

  const stateLabel = saving
    ? t('common.form.saving')
    : isNew
      ? t('cases.legSave.unsavedLeg')
      : dirty || held
        ? t('cases.legSave.unsavedChanges')
        : t('cases.legSave.saved')

  const asksDetail =
    canRate && (detail.candidates.length > 0 || lines.some((line) => line.id === detail.currentParent))
  const heldAnswerLabel =
    heldAnswer === undefined
      ? undefined
      : heldAnswer === ''
        ? t('cases.legs.vehiclesTable.detailOfNone')
        : isParentLine(heldAnswer)
          ? (lines.find((line) => line.id === heldAnswer)?.description ?? '—')
          : '—'
  const detailQuestion =
    heldAnswerLabel !== undefined && canRate && (asksDetail || heldAnswer !== UNSET_PARENT) ? (
      <HeldDetailAnswer label={heldAnswerLabel} />
    ) : asksDetail ? (
      <label className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="text-text-muted text-[13px]">{t('cases.legSave.detailOf')}</span>
        <Select
          selectSize="sm"
          className="min-w-56"
          value={detail.chosenParent}
          onChange={(value) => setParentChoice(value)}
          placeholder={t('cases.legs.vehiclesTable.detailOfPlaceholder')}
          aria-label={t('cases.legSave.detailOf')}
          aria-invalid={detail.mustChoose || undefined}
          options={[
            { value: '', label: t('cases.legs.vehiclesTable.detailOfNone') },
            ...detail.candidates.map((line) => ({
              value: line.id,
              label: line.description,
              hint: formatMoney(line.planned.amount, line.planned.currency),
            })),
            ...(isParentLine(detail.currentParent) &&
            !detail.candidates.some((line) => line.id === detail.currentParent)
              ? lines
                  .filter((line) => line.id === detail.currentParent)
                  .map((line) => ({
                    value: line.id,
                    label: line.description,
                    hint: formatMoney(line.planned.amount, line.planned.currency),
                  }))
              : []),
          ]}
        />
      </label>
    ) : undefined

  /* The identity refusal is not repeated here: it is `readOnlyReason`, said
     once above the cards like the page's other read-only reasons. */
  const notice = (
    <>
      {detail.warn && canRate ? (
        <Banner
          tone="attention"
          title={t('cases.legs.vehiclesTable.unlinkedWarning', {
            lines: detail.unlinkedCandidates
              .map((line) => t('cases.legs.vehiclesTable.quotedLine', { line: line.description }))
              .join(', '),
          })}
        >
          {readOnly ? null : t('cases.legSave.unlinkedHint')}
        </Banner>
      ) : null}
      {canRate && expenseLines.isError ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-negative min-w-0 flex-1 text-[12.5px] leading-snug">
            {t('cases.legs.vehiclesTable.planLinesFailed')}
          </p>
          <Button
            variant="secondary"
            size="sm"
            disabled={expenseLines.isFetching}
            onClick={() => void expenseLines.refetch()}
          >
            {t('common.actions.retry')}
          </Button>
        </div>
      ) : null}
    </>
  )

  return (
    <>
      <LegSummary
        draft={view}
        sequence={latest?.sequence}
        caseNumber={isNew ? record?.number : undefined}
        carriers={carriers}
        points={stripPoints}
        currentPointId={latest?.currentPointId}
        nextPointDueAt={latest?.nextPointDueAt}
        storedPoints={storedPoints}
        automated={automated}
        onOpenParams={() => openDrawer('route')}
        onPayCarrier={canPay ? () => setPayingCarrier(true) : undefined}
        /* On the route edit alone, not `readOnly`: marking the next point is
           its own route, writes no identity, and is a dispatcher's main act
           on a leg whose carrier they are not shown. */
        onMarkNext={
          routeReadOnly || !latest || currentIndex < 0
            ? undefined
            : () =>
                markPoints.mutate({
                  legId: latest.id,
                  payload: chainWithReachedCount(storedPoints, currentIndex + 2),
                })
        }
        markPending={markPoints.isPending}
      />

      {markPoints.error ? (
        <Banner tone="negative" title={t('cases.legSave.markFailed')}>
          {chainRefusal(markPoints.error, t)}
        </Banner>
      ) : null}

      <SaveReport
        failure={failure}
        conflict={conflict}
        held={held}
        unsent={unsent}
        pending={saving}
        blockedReason={blockedReason}
        onRetry={() => void save()}
      />

      {readOnly && readOnlyReason ? (
        <p className="text-text-muted text-[12.5px] leading-snug">{readOnlyReason}</p>
      ) : null}

      {!moneyForbidden && (composed?.money !== undefined || canRate) ? (
        <LegWholeCard
          carrierName={view.carrierName}
          showIdentity={showIdentity && !identityHidden}
          plans={legPlans}
          legLevel={composed?.money?.legLevel}
          editable={canRate}
          rate={legRate}
          onRateChange={setLegRate}
          heldRate={heldLegRate}
          invalid={badLegRate}
        />
      ) : null}

      {kinds.map((kind, index) => (
        <UnitsCard
          key={kind}
          caseId={caseId}
          leg={composed}
          kind={kind}
          rows={rows}
          onRowsChange={(next) => setRowsState({ ...rowsState, rows: next })}
          defaults={rowDefaults(kind)}
          onDefaultsChange={changeDefaults(kind)}
          showIdentity={showIdentity && !identityHidden}
          canRate={canRate}
          canPay={canPay}
          /* `readOnly` covers a reader refused the leg's identity (AD-6): the
             cards are text for them, not inputs whose edit no press can send
             (S-18b AC #15, #133). */
          readOnly={readOnly}
          planned={planned}
          moneyHeld={moneyHeld}
          bookedRate={bookedRate}
          heldRate={(unitId) => held?.rates?.find(({ line }) => line.unitId === unitId)?.line.amount}
          remaining={remaining}
          problems={problems}
          rowErrors={rowErrors}
          rateMissingCurrency={rateMissingCurrency}
          /* The record-level sentences about the units, once, above the
             first card. */
          notice={index === 0 ? notice : undefined}
        />
      ))}

      {readOnly ? null : (
        <AddUnitKind
          kinds={UNIT_KINDS.filter((kind) => !kinds.includes(kind))}
          onAdd={(kind) => setAddedKinds([...addedKinds, kind])}
        />
      )}

      {composed ? (
        <LegFinance caseId={caseId} leg={composed} onRetry={onRetryMoney} />
      ) : isNew && !moneyForbidden ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-text-muted">{t('cases.legFinance.title')}</CardTitle>
          </CardHeader>
          <CardBody className="pt-2.5">
            <p className="text-text-muted text-[13px] leading-snug font-light">{t('cases.legNew.moneyLater')}</p>
          </CardBody>
        </Card>
      ) : null}

      {readOnly ? null : (
        <LegSaveBar
          state={stateLabel}
          middle={detailQuestion}
          primaryLabel={isNew ? t('cases.legNew.create') : t('common.actions.save')}
          onPrimary={() => void save()}
          blockedReason={blockedReason}
          pending={saving}
          onCancel={cancel}
          confirmCancel={isNew ? fieldsDirty || unitsDirty || chain !== null : dirty || held !== null}
          fix={
            missing.length > 0
              ? {
                  label: t('cases.legSave.missingFix', { fields: missing.join(', ') }),
                  onClick: () => openDrawer(missingTab),
                }
              : undefined
          }
        />
      )}

      {/* «Оплатить перевозчику» from the summary: no unit implied, so the
          sheet opens on the leg's carrier with nothing ticked. A unit row's
          own button opens its own (`UnitsCard`). */}
      {canPay && composed?.money ? (
        <CarrierPaymentSheet
          open={payingCarrier}
          onClose={() => setPayingCarrier(false)}
          caseId={caseId}
          leg={composed}
          money={composed.money}
        />
      ) : null}

      <LegParamsDrawer
        open={drawer.open}
        tab={drawer.tab}
        onClose={closeDrawer}
        value={drawerValue}
        onDone={applyParams}
        caseId={caseId}
        leg={latest}
        readOnly={readOnly}
        readOnlyReason={readOnlyReason}
        canManageTemplates={canManageTemplates}
        onDeleted={onLeave}
      />
    </>
  )
}

/** «Ставки детализируют», frozen while rate lines are held: the answer they
 *  carry, read-only, with what «Повторить» and «Отмена» do to it — the held
 *  rate's own note, said of the answer. A hover title on a desk; on a phone,
 *  where nothing hovers, the whole sentence is written out. */
function HeldDetailAnswer({ label }: { label: string }) {
  const { t } = useTranslation()
  const wide = useTableFits()
  const short = t('cases.legSave.detailOfHeld')
  const hint = t('cases.legSave.detailOfHeldHint')
  return (
    <div
      role="group"
      aria-label={t('cases.legSave.detailOf')}
      className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5"
    >
      <span className="text-text-muted text-[13px]">{t('cases.legSave.detailOf')}</span>
      <span className="text-text-body text-[13px]">{label}</span>
      <span title={wide ? hint : undefined} className="text-text-muted text-[12px] leading-snug">
        {wide ? short : `${short}. ${hint}`}
      </span>
    </div>
  )
}

/** The units of this leg that already carry a per-unit planned line — read
 *  off the case's raw cost lines. Their rate is shown, never asked again. */
function plannedUnits(lines: readonly ExpenseLine[], legId: string): Set<string> {
  return new Set(
    lines.flatMap((line) =>
      line.legId === legId && line.unitId !== undefined && !line.unplanned ? [line.unitId] : [],
    ),
  )
}

/** The keys of the rows a leg write sends, in `unitsPayload`'s order — what a
 *  served `units.<i>` index names. */
function sentKeysOf(rows: readonly UnitRow[]): string[] {
  return UNIT_KINDS.flatMap((kind) =>
    rows.filter((row) => row.kind === kind && !row.pristine && row.number.trim() !== '').map((row) => row.key),
  )
}

/** What a press left behind, in words, ABOVE the record — never a toast: a
 *  page that navigated on the create has nowhere else to say it. Each failed
 *  step is its own sentence, because each is a different thing to do next. */
function SaveReport({
  unsent,
  ...report
}: {
  failure: { step: Step; error: unknown } | null
  conflict: { vanished: NamedUnit[] } | null
  held: HeldSteps | null
  /** Units whose rate the press did not send: they were not on the leg. */
  unsent: NamedUnit[]
  pending: boolean
  /** The save bar's refusal: «Повторить» is the same press, refused alike. */
  blockedReason: string | undefined
  onRetry: () => void
}) {
  const { t } = useTranslation()
  return (
    <>
      {unsent.length > 0 ? (
        <Banner
          tone="attention"
          title={t('cases.legSave.ratesUnsent', {
            count: unsent.length,
            units: unsent.map((unit) => unitLabel(t, unit)).join(', '),
          })}
        />
      ) : null}
      <StepReport {...report} />
    </>
  )
}

/** The step a press stopped at, or the conflict it met — one sentence. */
function StepReport({
  failure,
  conflict,
  held,
  pending,
  blockedReason,
  onRetry,
}: {
  failure: { step: Step; error: unknown } | null
  conflict: { vanished: NamedUnit[] } | null
  held: HeldSteps | null
  pending: boolean
  blockedReason: string | undefined
  onRetry: () => void
}) {
  const { t } = useTranslation()

  if (conflict) {
    return (
      <Banner tone="attention" title={t('cases.legSave.unitsChanged')}>
        {conflict.vanished.length > 0
          ? t('cases.legSave.unitsVanished', {
              units: conflict.vanished.map((unit) => unitLabel(t, unit)).join(', '),
            })
          : null}
      </Banner>
    )
  }
  if (!failure) return null

  const retry = (
    <Button variant="secondary" size="sm" disabled={pending} blockedReason={blockedReason} onClick={onRetry}>
      {t('cases.legSave.retry')}
    </Button>
  )
  const sentence = (error: unknown) => refusalMessage(error, t)

  /* A body key the server no longer reads (`retired`, S-18b) is only ever
     sent by a page older than the deploy: nothing was written, and reloading
     is the whole fix — said, with the act beside it. */
  if (Object.values(fieldErrors(failure.error)).some((codes) => codes.includes('retired'))) {
    return (
      <Banner
        tone="negative"
        title={t('cases.legSave.legFailed')}
        action={
          <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>
            {t('cases.legSave.reload')}
          </Button>
        }
      >
        {t('common.form.retired')}
      </Banner>
    )
  }

  switch (failure.step) {
    case 'leg':
      return <Banner tone="negative" title={t('cases.legSave.legFailed')}>{sentence(failure.error)}</Banner>
    case 'chain':
      return (
        <Banner tone="negative" title={t('cases.legSave.chainFailed')} action={retry}>
          {chainRefusal(failure.error, t)}
        </Banner>
      )
    case 'rates':
      return (
        <Banner
          tone="negative"
          title={t('cases.legs.vehiclesTable.savedTrucksRatesFailed', { count: held?.rates?.length ?? 0 })}
          action={retry}
        >
          {sentence(failure.error)}
        </Banner>
      )
    case 'refile':
      return (
        <Banner tone="negative" title={t('cases.legs.vehiclesTable.detailOfFailed')} action={retry}>
          {sentence(failure.error)}
        </Banner>
      )
    case 'template':
      return (
        <Banner tone="attention" title={t('cases.legSave.templateFailed')} action={retry}>
          {sentence(failure.error)}
        </Banner>
      )
  }
}

/** A refused chain write — the prefix violation gets its own sentence. */
function chainRefusal(error: unknown, t: TFunction): string {
  if (error instanceof ApiError) {
    const fields = error.problem.errors ?? {}
    if (Object.entries(fields).some(([field, codes]) => field.endsWith('.reached') && codes.includes('not-contiguous'))) {
      return t('cases.legPage.notContiguous')
    }
  }
  return refusalMessage(error, t)
}

